/**
 * MyCardsPanel — profile-page UI for managing card-data integrations.
 *
 * The user sees all their own card_connections here. From this one place
 * they can:
 *   - Connect a new card (BankID flow opens the aggregator in a new tab)
 *   - Re-authenticate when SCA is about to expire
 *   - Disconnect a card
 *   - Toggle per-household sharing
 *
 * The component is route-agnostic so we can also embed it on a future
 * dedicated profile route or in settings.
 */
import type { AppRouter } from "@fellesen/api/router";
import type { inferRouterOutputs } from "@trpc/server";
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { t } from "../i18n";
import { cn } from "../lib/cn";
import { formatFromNow, formatRelative } from "../lib/format";
import { trpc } from "../lib/trpc";
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardDescription,
  CardTitle,
  Dialog,
  DropdownMenu,
  EmptyState,
  Field,
  FieldMessage,
  highlightMatch,
  Icon,
  Input,
  PageHeader,
  SkeletonGate,
  Spinner,
  Stack,
  Switch,
  TapTarget,
  useToast,
} from "./ui";
import { SASAmexElite, SASEuroBonusMastercard } from "./WalletCards";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ConnectionListItem = RouterOutputs["cardConnections"]["list"][number];
type HouseholdListItem = RouterOutputs["household"]["list"][number];

/**
 * The body of Mine kort — connection list + connect-dialog. Used both
 * by the dedicated route (MyCardsPanel) and the Mine kort modal opened
 * from the user menu (AppShell). In embedded mode we drop the page
 * header (the Dialog renders its own title), surface the "Koble til
 * kort"-CTA at the bottom of the list, and accept an `onClose` so we
 * can offer the user an explicit "Lukk"-button next to the CTA.
 */
export function MyCardsContent({
  embedded = false,
  onClose,
}: {
  embedded?: boolean;
  onClose?: () => void;
} = {}) {
  const connections = trpc.cardConnections.list.useQuery();
  const households = trpc.household.list.useQuery();
  const me = trpc.auth.me.useQuery();
  const [connectOpen, setConnectOpen] = useState(false);

  const displayName = me.data?.displayName ?? me.data?.email ?? null;

  // In embedded mode the "Koble til kort"-CTA lives at the bottom of the
  // panel, after the existing connections. The EmptyState owns its own
  // inline button when no connections exist, so the bottom CTA only
  // shows up when the user already has at least one card.
  const hasConnections = (connections.data?.length ?? 0) > 0;

  return (
    <>
      <SkeletonGate
        loading={connections.isPending}
        shape="cards"
        count={2}
        cardColumns={1}
        label={t.common.loading}
      >
        {connections.isError && (
          <Card padding="lg">
            <FieldMessage tone="error">{connections.error.message}</FieldMessage>
          </Card>
        )}

        {connections.data?.length === 0 && (
          <Card padding="none">
            <EmptyState
              icon="credit-card"
              title={t.cards.emptyTitle}
              description={t.cards.emptyDescription}
              action={
                <Button variant="primary" iconLeft="plus" onClick={() => setConnectOpen(true)}>
                  {t.cards.connectButton}
                </Button>
              }
            />
          </Card>
        )}

        {connections.data && connections.data.length > 0 && (
          <Stack gap={3}>
            {connections.data.map((conn) => (
              <ConnectionCard
                key={conn.id}
                connection={conn}
                households={households.data ?? []}
                displayName={displayName}
              />
            ))}
          </Stack>
        )}
      </SkeletonGate>

      {embedded && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {onClose && <Button onClick={onClose}>{t.common.close}</Button>}
          {hasConnections && (
            <Button variant="primary" iconLeft="plus" onClick={() => setConnectOpen(true)}>
              {t.cards.connectButton}
            </Button>
          )}
        </div>
      )}

      <ConnectDialog open={connectOpen} onClose={() => setConnectOpen(false)} />
    </>
  );
}

export default function MyCardsPanel() {
  return (
    <>
      <PageHeader
        icon="credit-card"
        title={t.cards.profileTitle}
        description={t.cards.profileDescription}
      />
      <MyCardsContent />
    </>
  );
}

// ---------------------------------------------------------------------------
// Connection card — status, accounts, share toggles, actions
// ---------------------------------------------------------------------------

type CardVariant = "sas-mc" | "amex";

function pickCardVariant(aspspName: string): CardVariant | null {
  const name = aspspName.toLowerCase();
  if (name.includes("amex") || name.includes("american express")) return "amex";
  if (name.includes("sas")) return "sas-mc";
  return null;
}

/** Measures the container so we can scale the wallet card down on narrow screens. */
function useContainerWidth() {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

type ConnectionState = "active" | "renewal" | "expired";

/**
 * Derive the effective UI state from the persisted `status` + `expiresAt`.
 * The DB status column isn't auto-transitioned when consent expires, so we
 * compute the "displayed" state in the frontend to keep the badge + expiry
 * line consistent.
 */
function deriveConnectionState(connection: ConnectionListItem): ConnectionState {
  const expiresAtMs = new Date(connection.expiresAt).getTime();
  if (connection.status !== "active" || expiresAtMs < Date.now()) return "expired";
  if (connection.needsRenewal) return "renewal";
  return "active";
}

function ConnectionCard({
  connection,
  households,
  displayName,
}: {
  connection: ConnectionListItem;
  households: HouseholdListItem[];
  displayName: string | null;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const disconnect = trpc.cardConnections.delete.useMutation({
    onSuccess: () => {
      utils.cardConnections.list.invalidate();
      setConfirmDeleteOpen(false);
      show({ tone: "info", title: `${connection.aspspName} koblet fra.`, dismissLabel: "OK" });
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.actions.disconnectFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const state = deriveConnectionState(connection);

  const lastSyncedLabel = connection.lastSyncedAt
    ? t.cards.lastSyncedAt(formatRelative(connection.lastSyncedAt))
    : connection.status === "active"
      ? t.cards.lastSyncedReady
      : t.cards.lastSyncedNever;

  const reconnect = trpc.cardConnections.initConnect.useMutation({
    onSuccess: (result) => {
      window.location.href = result.authorizationUrl;
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.connectDialog.connectFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const handleRenew = () => {
    const redirectUrl = new URL("/cards/callback", window.location.origin).toString();
    reconnect.mutate({
      integrationId: connection.integrationId,
      aspspName: connection.aspspName,
      aspspCountry: connection.aspspCountry,
      aspspLogo: connection.aspspLogo ?? null,
      redirectUrl,
    });
  };

  const cardVariant = pickCardVariant(connection.aspspName);
  // Proper-case slik den lagres på brukeren. CardStack uppercaser den ved
  // wallet-kort-render der vi vil ha det fysiske kreditkort-formatet,
  // mens Avatar i tilordnings-dropdownen får samme streng som i
  // profilen så hash-fargen blir konsistent på tvers av UI-et.
  const ownerName = displayName ?? "Korteier";

  const headerRow = (
    // On mobile we stack the badges below the title row so long labels
    // ("Trenger ny BankID-autentisering" etc.) don't get squeezed against
    // the right edge. Desktop keeps the side-by-side layout for density.
    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        {connection.aspspLogo ? (
          <img
            src={connection.aspspLogo}
            alt=""
            className="size-10 rounded border border-ink-200 bg-white object-contain p-1"
          />
        ) : (
          <div className="flex size-10 items-center justify-center rounded border border-ink-200 bg-ink-50 text-ink-500">
            <Icon name="credit-card" size="md" />
          </div>
        )}
        <div className="min-w-0">
          <CardTitle className="truncate">{connection.aspspName}</CardTitle>
          <CardDescription>{lastSyncedLabel}</CardDescription>
        </div>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5 md:justify-end">
        <StatusBadge state={state} />
        {state === "active" && (
          <Badge tone="neutral" size="sm">
            {t.cards.expiresIn(formatFromNow(connection.expiresAt))}
          </Badge>
        )}
        {state === "renewal" && (
          <Badge tone="warning" size="sm" iconLeft="alert-circle">
            {t.cards.expiresIn(formatFromNow(connection.expiresAt))}
          </Badge>
        )}
        {state === "expired" && (
          <Badge tone="danger" size="sm" iconLeft="alert-circle">
            {t.cards.expiresExpiredCta}
          </Badge>
        )}
      </div>
    </div>
  );

  const sharingMaybe = connection.status === "active" && (
    <SharingPanel connection={connection} households={households} />
  );

  // Action buttons rendered next to the cardholder-assignment row inside
  // CardStack. "Autentiser på nytt" comes first when the connection needs
  // re-authentication, then "Koble fra" last as the destructive action.
  // Same content is wrapped in a justify-end row for connections without
  // a card visual (no `cardVariant`) so the buttons still appear.
  const cardStackActions = (
    <>
      {state !== "active" && (
        <Button
          size="sm"
          variant="primary"
          iconLeft="refresh-cw"
          isLoading={reconnect.isPending}
          onClick={handleRenew}
        >
          {t.cards.actions.reconnect}
        </Button>
      )}
      <Button size="sm" iconLeft="log-out" onClick={() => setConfirmDeleteOpen(true)}>
        {t.cards.actions.disconnect}
      </Button>
    </>
  );

  const actionRow = (
    <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
      {cardStackActions}
    </div>
  );

  const [mobileSlotRef, mobileSlotWidth] = useContainerWidth();
  const [desktopSlotRef, desktopSlotWidth] = useContainerWidth();
  // Width caps tuned down a touch — the dialog is wide enough that a 320px
  // card looks bulky next to the share/action column.
  const mobileCardWidth = Math.max(200, Math.min(280, mobileSlotWidth || 260));
  const desktopCardWidth = Math.max(220, Math.min(280, desktopSlotWidth || 260));

  const renderCardStack = (width: number) => {
    if (!cardVariant) return null;
    return (
      <CardStack
        connectionId={connection.id}
        cardVariant={cardVariant}
        accounts={connection.accounts}
        ownerName={ownerName}
        width={width}
      />
    );
  };

  // Utløpsinfo lever nå utelukkende som badge ved siden av StatusBadge i
  // headerRow (alle tre tilstander). Behold ingen redundant bunntekst.

  return (
    <Card padding="md">
      <Stack gap={5}>
        {headerRow}
        {cardVariant && (
          <div ref={mobileSlotRef} className="flex w-full justify-center py-2 md:hidden">
            {renderCardStack(mobileCardWidth)}
          </div>
        )}
        {cardVariant ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="hidden min-w-0 flex-col gap-3 md:flex">
              <div ref={desktopSlotRef} className="flex w-full justify-start">
                {renderCardStack(desktopCardWidth)}
              </div>
            </div>
            {/* Right column matches the card column's height (default
                `items-stretch` on the grid); actionRow uses `mt-auto`
                so it sticks to the bottom of the container. */}
            <div className="flex min-w-0 flex-col gap-3">
              {sharingMaybe}
              <div className="mt-auto">{actionRow}</div>
            </div>
          </div>
        ) : (
          <>
            {sharingMaybe}
            {actionRow}
          </>
        )}
      </Stack>

      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        title={t.cards.actions.disconnect}
        description={t.cards.actions.disconnectConfirm(connection.aspspName)}
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmDeleteOpen(false)} disabled={disconnect.isPending}>
              {t.common.cancel}
            </Button>
            <Button
              variant="primary"
              isLoading={disconnect.isPending}
              onClick={() => disconnect.mutate({ connectionId: connection.id })}
            >
              {t.cards.actions.disconnect}
            </Button>
          </>
        }
      />
    </Card>
  );
}

function StatusBadge({ state }: { state: ConnectionState }) {
  // Tre tilstander (utledet i `deriveConnectionState`):
  //   active  → grønn check, "Aktiv"
  //   renewal → gul alert, "Trenger fornyelse" (≤14 dager igjen)
  //   expired → rød alert, "Utløpt" (passert eller status != "active")
  if (state === "active") {
    return (
      <Badge tone="success" size="sm" iconLeft="check">
        {t.cards.status.active}
      </Badge>
    );
  }
  if (state === "renewal") {
    return (
      <Badge tone="warning" size="sm" iconLeft="alert-circle">
        {t.cards.status.renewal}
      </Badge>
    );
  }
  return (
    <Badge tone="danger" size="sm" iconLeft="alert-circle">
      {t.cards.status.expired}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// CardStack — one connection can show multiple cards (hovedkort + ekstrakort)
// ---------------------------------------------------------------------------

/**
 * Renders every observed card on a connection: the primary card on the
 * account (always present, derived from card_accounts.mask) plus any
 * ekstrakort discovered from live transactions (distinct cardholder /
 * last-4 tuples). Each ekstrakort gets an assignment-to-member control so
 * the user can tag who actually uses it — without that mapping every
 * transaction is silently attributed to the connection owner.
 *
 * The visual hierarchy is intentional: hovedkortet sits at full scale,
 * ekstrakort er litt mindre (0.92×) og tydelig merket. Tilordnings-UI-en
 * ligger under hvert ekstrakort så bruker kan løse "hvem er dette" en gang
 * og slippe å gjøre det per transaksjon.
 */
type ObservedCard = {
  accountId: string;
  accountMask: string | null;
  cardholderName: string | null;
  cardLastFour: string | null;
  isPrimary: boolean;
  mappings: Array<{
    groupId: string;
    memberId: string;
    memberUserId: string;
    memberName: string | null;
  }>;
};

/**
 * Horisontal swiper-stil galleri av kortene knyttet til en tilkobling.
 * Bruker native CSS scroll-snap så swipe på touch og piltastnavigasjon på
 * desktop fungerer uten kustom drag-håndtering. Aktivt kort = det som er
 * sentrert i scroll-viewporten, bestemt av en throttlet scroll-handler.
 */
function CardStack({
  connectionId,
  cardVariant,
  accounts,
  ownerName,
  width,
  rightActions,
}: {
  connectionId: string;
  cardVariant: CardVariant;
  accounts: ConnectionListItem["accounts"];
  ownerName: string;
  width: number;
  /** Right-side slot rendered on the same row as the cardholder-assignment
   * dropdown — typically the "Koble fra"-button so primary actions live
   * close to the card visual. */
  rightActions?: ReactNode;
}) {
  const cardsQuery = trpc.cardConnections.listCardsForConnection.useQuery({ connectionId });
  const membersQuery = trpc.cardConnections.listAssignableMembers.useQuery({ connectionId });

  // Hovedkort-fallback før første synk: bruk masken fra card_accounts.
  // Hopper over kontoer uten mask — vi vil ikke flashe et "··0000"-kort
  // mens vi venter på at backend skal svare. Hvis ingen kontoer har mask
  // viser vi heller ingenting før listCardsForConnection er ferdig.
  const fallbackPrimary = useMemo<ObservedCard[]>(() => {
    return accounts
      .filter((a) => a.mask !== null && a.mask !== "")
      .map((a) => ({
        accountId: a.id,
        accountMask: a.mask,
        cardholderName: ownerName,
        cardLastFour: a.mask,
        isPrimary: true,
        mappings: [],
      }));
  }, [accounts, ownerName]);

  const observed: ObservedCard[] = cardsQuery.data ?? fallbackPrimary;

  // Hovedkortet alltid først → aktiv-snap lander der ved første render.
  const sorted = useMemo(() => {
    const list = [...observed];
    list.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    return list;
  }, [observed]);

  // Track the scroller via state so the scroll-listener useEffect re-runs
  // when the element mounts. A plain useRef wouldn't be enough: the
  // scroller only appears in the DOM AFTER cardsQuery.isPending flips to
  // false, and useEffect with [] deps runs once at mount when the ref
  // is still null — leaving the scroll listener detached and breaking
  // touch-swipe-driven activeIndex updates.
  const [scrollerEl, setScrollerEl] = useState<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // While we're driving the scroller programmatically (arrow-click), we
  // suppress the scroll-position-based active-index detection. Otherwise
  // intermediate frames during the smooth animation flip the counter to
  // the wrong neighbour. Cleared by a timeout sized to the typical smooth
  // scroll duration (~500ms) plus snap settling.
  const suppressScrollUpdateRef = useRef(false);
  const suppressTimerRef = useRef<number | null>(null);

  // Detect which card is currently centred in the scroller. rAF-throttled
  // so we don't re-render on every scroll event. The handler reads
  // el.children live, so the listener does not need to re-attach when the
  // list shrinks/grows.
  useEffect(() => {
    const el = scrollerEl;
    if (!el) return;
    let raf = 0;
    const update = () => {
      if (suppressScrollUpdateRef.current) return;
      const viewportCenter = el.scrollLeft + el.clientWidth / 2;
      let bestIdx = 0;
      let bestDist = Number.POSITIVE_INFINITY;
      for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.clientWidth / 2;
        const d = Math.abs(childCenter - viewportCenter);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      setActiveIndex(bestIdx);
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      el.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
      if (suppressTimerRef.current !== null) {
        window.clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = null;
      }
    };
  }, [scrollerEl]);

  // Klampe activeIndex hvis listen krymper (f.eks. dedup fjernet et kort).
  useEffect(() => {
    if (activeIndex >= sorted.length && sorted.length > 0) {
      setActiveIndex(0);
    }
  }, [activeIndex, sorted.length]);

  const scrollToIndex = (i: number) => {
    const el = scrollerEl;
    if (!el) return;
    const child = el.children[i] as HTMLElement | undefined;
    if (!child) return;
    // Eagerly sync state to the click so the counter + arrow-disabled
    // state never drift from what the user clicked.
    setActiveIndex(i);
    // Suppress the scroll-position-driven detector while the smooth
    // animation runs — intermediate frames would otherwise reset
    // activeIndex to the wrong neighbour mid-flight.
    suppressScrollUpdateRef.current = true;
    if (suppressTimerRef.current !== null) {
      window.clearTimeout(suppressTimerRef.current);
    }
    suppressTimerRef.current = window.setTimeout(() => {
      suppressScrollUpdateRef.current = false;
      suppressTimerRef.current = null;
    }, 600);

    // scrollIntoView is more robust than manual scrollTo for snap-mandatory
    // containers nested in a <dialog>: the browser computes the correct
    // scroll position from the child's bounding box (no stale clientWidth
    // measurements), respects scroll-snap-align, and cancels any prior
    // smooth-scroll animation cleanly on consecutive calls. `block: "nearest"`
    // avoids scrolling the dialog vertically when the card is already in view.
    child.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  };

  if (cardsQuery.isError) {
    return (
      <FieldMessage tone="error">
        {t.cards.cardLabels.loadFailed(cardsQuery.error.message)}
      </FieldMessage>
    );
  }

  // Initial last: vis spinner istedenfor fallback-kortet, så brukeren ser
  // hovedkort + alle ekstrakort samtidig når data er ferdig — istedenfor
  // at hovedkortet flasher kort, etterfulgt av ekstrakort som plutselig
  // dukker opp.
  if (cardsQuery.isPending) {
    return (
      <div className="flex w-full justify-center py-12">
        <Spinner size="md" />
      </div>
    );
  }

  if (sorted.length === 0) return null;

  const activeCard = sorted[activeIndex];
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < sorted.length - 1;

  return (
    <div className="flex w-full flex-col items-center gap-3">
      {/* Scroller — snap-x mandatory så hver swipe lander på neste kort.
          Hver "slide" er full container-bredde; selve wallet-kortet er
          sentrert inni. Skjuler scrollbar for ren swiper-følelse. */}
      <div
        ref={setScrollerEl}
        // `touch-pan-x` tells mobile browsers that horizontal pan belongs
        // to this scroller — without it, iOS Safari sometimes routes the
        // gesture up to the parent <dialog> content (which scrolls
        // vertically), blocking horizontal swipe. `overscroll-x-contain`
        // stops a swipe past the first/last card from bubbling up as a
        // back-gesture or dialog-pan.
        className="flex w-full touch-pan-x snap-x snap-mandatory overflow-x-auto overscroll-x-contain scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {sorted.map((card, idx) => {
          // Wallet-kortet bruker UPPERCASE for å matche fysisk kredittkort-
          // typografi. ownerName er proper-case for at avatarer/profil-
          // visning skal være konsistente — uppercase kun her på rendring.
          const visualName = card.cardholderName
            ? card.cardholderName.toUpperCase()
            : card.isPrimary
              ? ownerName.toUpperCase()
              : "EKSTRAKORT";
          const visualLastFour = card.cardLastFour ?? card.accountMask ?? "0000";
          const visualNumber = `•••• •••• •••• ${visualLastFour}`;
          const cardKey = `${card.accountId}|${card.cardholderName ?? ""}|${card.cardLastFour ?? ""}|${idx}`;

          return (
            <div
              key={cardKey}
              className="flex w-full shrink-0 snap-center justify-center px-2 py-2"
            >
              <div className="relative">
                {cardVariant === "amex" ? (
                  <SASAmexElite
                    cardNumber={visualNumber}
                    holderName={visualName}
                    width={width}
                  />
                ) : (
                  <SASEuroBonusMastercard
                    cardNumber={visualNumber}
                    holderName={visualName}
                    width={width}
                  />
                )}
                <CardRoleBadge card={card} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigasjon: [←]  1 / 2  [→]  under stokken. */}
      {sorted.length > 1 && (
        <div className="flex items-center gap-3">
          <CardStackArrow
            direction="left"
            ariaLabel="Forrige kort"
            disabled={!canPrev}
            onClick={() => scrollToIndex(activeIndex - 1)}
          />
          <span
            className="min-w-[3rem] text-center font-medium text-ink-600 text-sm tabular-nums"
            aria-live="polite"
          >
            {activeIndex + 1} / {sorted.length}
          </span>
          <CardStackArrow
            direction="right"
            ariaLabel="Neste kort"
            disabled={!canNext}
            onClick={() => scrollToIndex(activeIndex + 1)}
          />
        </div>
      )}

      {/* Assignment row for the active card. Always visible so the user
          sees who uses each card; primary card shows the owner as a locked
          value, ekstrakort has an active dropdown. Centered under the
          card with extra top spacing so it doesn't crowd the navigation
          pills above. `rightActions` (typically connection actions) wraps
          right when present, but in the current layout it's empty
          (actions live in the right column at panel level). */}
      {activeCard && (
        <div className="mt-3 flex w-full flex-wrap items-center justify-center gap-3">
          <CardAssignmentControl
            connectionId={connectionId}
            card={activeCard}
            ownerName={ownerName}
            members={membersQuery.data ?? []}
            membersLoading={membersQuery.isPending}
          />
          {rightActions && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {rightActions}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Sidepil-knapp som flyter over scrolleren. Disablet i endene så
 * brukeren får visuelt signal om at det ikke er flere kort i den retningen.
 */
function CardStackArrow({
  direction,
  ariaLabel,
  disabled,
  onClick,
  className,
}: {
  direction: "left" | "right";
  ariaLabel: string;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-full border border-ink-200 bg-white p-1.5 text-ink-700 shadow-sm transition-colors hover:bg-ink-50 focus-visible:bg-ink-50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 dark:bg-ink-50 dark:text-ink-800",
        className,
      )}
    >
      <Icon name={direction === "left" ? "chevron-left" : "chevron-right"} size="sm" />
    </button>
  );
}

/** Badge øverst på et wallet-kort som indikerer rolle (Hovedkort / Ekstrakort / "Tilhører N"). */
function CardRoleBadge({
  card,
}: {
  card: {
    isPrimary: boolean;
  };
}) {
  // Badgen viser KORTETS rolle (hovedkort vs ekstrakort). Hvem som eier
  // ekstrakortet vises i tilordnings-dropdownen under kortet, så vi ikke
  // dupliserer "Tilhører X" på selve kortet.
  if (card.isPrimary) {
    return (
      <div className="absolute -top-2 right-2">
        <Badge tone="success" size="sm">
          {t.cards.cardLabels.mainCard}
        </Badge>
      </div>
    );
  }
  return (
    <div className="absolute -top-2 right-2">
      <Badge tone="info" size="sm">
        {t.cards.cardLabels.extraCard}
      </Badge>
    </div>
  );
}

/** Dropdown under hvert ekstrakort som lar bruker tilordne kortet til et husstandsmedlem. */
function CardAssignmentControl({
  connectionId,
  card,
  ownerName,
  members,
  membersLoading,
}: {
  connectionId: string;
  card: {
    cardholderName: string | null;
    cardLastFour: string | null;
    isPrimary: boolean;
    mappings: Array<{
      groupId: string;
      memberId: string;
      memberUserId: string;
      memberName: string | null;
    }>;
  };
  ownerName: string;
  members: Array<{ groupId: string; memberId: string; displayName: string | null }>;
  membersLoading: boolean;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const assign = trpc.cardConnections.assignCardholder.useMutation({
    onSuccess: (_, vars) => {
      utils.cardConnections.listCardsForConnection.invalidate({ connectionId });
      const memberName = members.find((m) => m.memberId === vars.memberId)?.displayName ?? "medlem";
      show({
        tone: "success",
        title: t.cards.cardLabels.assignSuccess(memberName, card.cardLastFour),
        dismissLabel: "OK",
      });
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.cardLabels.assignFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });
  const unassign = trpc.cardConnections.unassignCardholder.useMutation({
    onSuccess: () => {
      utils.cardConnections.listCardsForConnection.invalidate({ connectionId });
      show({
        tone: "info",
        title: t.cards.cardLabels.unassignSuccess,
        dismissLabel: "OK",
      });
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.cardLabels.unassignFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const firstMapping = card.mappings[0] ?? null;
  const isAssigned = firstMapping !== null;
  const busy = assign.isPending || unassign.isPending;

  const onPick = (value: string) => {
    // value form: "{groupId}|{memberId}"
    const [groupId, memberId] = value.split("|");
    if (!groupId || !memberId) return;
    assign.mutate({
      connectionId,
      groupId,
      memberId,
      cardholderName: card.cardholderName,
      cardLastFour: card.cardLastFour,
    });
  };

  const onUnassign = () => {
    if (!firstMapping) return;
    unassign.mutate({
      connectionId,
      groupId: firstMapping.groupId,
      cardholderName: card.cardholderName,
      cardLastFour: card.cardLastFour,
    });
  };

  const selectedLabel = card.isPrimary
    ? ownerName
    : (firstMapping?.memberName ?? null);

  // Picker is disabled when nothing is selectable: primary card (locked
  // to owner), in-flight mutation, members still loading, or no shared
  // households means no members to pick from. The last case is the most
  // common reason the user sees "can't open dropdown" — the connection
  // hasn't been shared with a household yet.
  const pickerDisabled =
    card.isPrimary || busy || membersLoading || (!card.isPrimary && members.length === 0);

  return (
    // Stack prompt + dropdown vertically on mobile (the picker pill is
    // wide enough that side-by-side cramps the row). Both centered so the
    // dropdown lines up under the prompt. Desktop keeps the inline layout.
    <div className="flex flex-col items-center gap-2 text-sm md:flex-row md:items-center">
      <span className="flex items-center gap-2 text-ink-600">
        <Icon
          name={card.isPrimary || isAssigned ? "user" : "user-plus"}
          size="xs"
          className="text-ink-500"
        />
        {t.cards.cardLabels.extraCardAssignPrompt}
      </span>
      <CardholderPicker
        selectedLabel={selectedLabel}
        members={members}
        disabled={pickerDisabled}
        canUnassign={isAssigned}
        placeholder={membersLoading ? "…" : t.cards.cardLabels.pickMember}
        onPick={onPick}
        onClear={onUnassign}
      />
    </div>
  );
}

/**
 * Dropdown-velger for kortholder — speiler stilen til CategoryPicker
 * i utgiftstabellen (DropdownMenu + TapTarget med pill-trigger). Brukes
 * både for ekstrakort (aktiv) og hovedkort (disabled, viser eier).
 */
function CardholderPicker({
  selectedLabel,
  members,
  disabled,
  canUnassign,
  placeholder,
  onPick,
  onClear,
}: {
  selectedLabel: string | null;
  members: Array<{ groupId: string; memberId: string; displayName: string | null }>;
  disabled: boolean;
  canUnassign: boolean;
  placeholder: string;
  onPick: (value: string) => void;
  onClear: () => void;
}) {
  // Disabled-tilstand: stille pill med navnet på den låste eieren.
  if (disabled) {
    return (
      <span className="inline-flex h-8 min-w-[10rem] max-w-[14rem] cursor-not-allowed items-center gap-2 rounded-md border border-ink-200 bg-ink-50 px-2 text-ink-700 text-xs opacity-90 dark:bg-ink-100">
        {selectedLabel ? (
          <>
            <Avatar name={selectedLabel} size="sm" />
            <span className="truncate">{selectedLabel}</span>
          </>
        ) : (
          <span className="truncate text-ink-400">{placeholder}</span>
        )}
      </span>
    );
  }

  return (
    <DropdownMenu
      align="start"
      trigger={
        <span className="inline-flex h-8 min-w-[10rem] max-w-[14rem] items-center gap-2 rounded-md border border-ink-200 bg-surface px-2 text-xs hover:bg-ink-50">
          {selectedLabel ? (
            <>
              <Avatar name={selectedLabel} size="sm" />
              <span className="truncate">{selectedLabel}</span>
            </>
          ) : (
            <span className="truncate text-ink-400">{placeholder}</span>
          )}
          <Icon name="chevron-down" size="xs" className="ml-auto text-ink-400" />
        </span>
      }
    >
      {members.map((m) => (
        <TapTarget
          key={`${m.groupId}|${m.memberId}`}
          role="menuitem"
          onClick={() => onPick(`${m.groupId}|${m.memberId}`)}
          className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-ink-700 text-sm hover:bg-ink-50"
        >
          <Avatar name={m.displayName ?? "?"} size="sm" />
          <span className="truncate">{m.displayName ?? "(uten navn)"}</span>
        </TapTarget>
      ))}
      {canUnassign && (
        <TapTarget
          role="menuitem"
          onClick={onClear}
          className="flex w-full items-center gap-2 border-ink-100 border-t px-3 py-1.5 text-left text-ink-500 text-sm hover:bg-ink-50"
        >
          <Icon name="x" size="xs" />
          <span>{t.cards.cardLabels.remove}</span>
        </TapTarget>
      )}
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Sharing toggles
// ---------------------------------------------------------------------------

function SharingPanel({
  connection,
  households,
}: {
  connection: ConnectionListItem;
  households: HouseholdListItem[];
}) {
  // Tracks whether to surface the post-share dialog asking the user
  // whether to also pull this connection's transactions into the
  // household's current-period oppgjør. Sharing is visibility-only; this
  // dialog turns it into a data import.
  const [postShare, setPostShare] = useState<{ groupId: string; groupName: string } | null>(null);

  return (
    <div>
      <div className="mb-2">
        <div className="font-medium text-ink-900 text-sm">{t.cards.sharing.title}</div>
        <p className="mt-0.5 text-ink-500 text-xs leading-relaxed">{t.cards.sharing.description}</p>
      </div>
      {households.length === 0 ? (
        <p className="text-ink-500 text-sm">{t.cards.sharing.empty}</p>
      ) : (
        <div className="divide-y divide-ink-100 overflow-hidden rounded-md border border-ink-100 dark:divide-ink-200 dark:border-ink-200">
          {households.map((h) => (
            <ShareToggleRow
              key={h.id}
              connectionId={connection.id}
              groupId={h.id}
              groupName={h.name}
              isShared={connection.sharedWithGroupIds.includes(h.id)}
              onJustShared={() => setPostShare({ groupId: h.id, groupName: h.name })}
            />
          ))}
        </div>
      )}
      <PostShareDialog
        open={postShare !== null}
        onClose={() => setPostShare(null)}
        connectionId={connection.id}
        aspspName={connection.aspspName}
        groupId={postShare?.groupId ?? ""}
        groupName={postShare?.groupName ?? ""}
      />
    </div>
  );
}

function ShareToggleRow({
  connectionId,
  groupId,
  groupName,
  isShared,
  onJustShared,
}: {
  connectionId: string;
  groupId: string;
  groupName: string;
  isShared: boolean;
  onJustShared: () => void;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();

  const share = trpc.cardConnections.share.useMutation({
    onSuccess: () => {
      utils.cardConnections.list.invalidate();
      // Sharing was visibility-only; offer to also pull transactions.
      onJustShared();
    },
    onError: (err) =>
      show({
        tone: "danger",
        title: t.cards.sharing.shareFailed(err.message),
        dismissLabel: "OK",
      }),
  });
  const unshare = trpc.cardConnections.unshare.useMutation({
    onSuccess: () => utils.cardConnections.list.invalidate(),
    onError: (err) =>
      show({
        tone: "danger",
        title: t.cards.sharing.unshareFailed(err.message),
        dismissLabel: "OK",
      }),
  });

  const busy = share.isPending || unshare.isPending;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-ink-50/60 dark:hover:bg-ink-100/30">
      <Avatar name={groupName} size="sm" />
      <div className="min-w-0 flex-1 truncate font-medium text-ink-800 text-sm">{groupName}</div>
      <Switch
        checked={isShared}
        disabled={busy}
        aria-label={t.cards.sharing.toggleLabel(groupName)}
        onCheckedChange={(next) => {
          if (next) share.mutate({ connectionId, groupId });
          else unshare.mutate({ connectionId, groupId });
        }}
      />
    </div>
  );
}

function PostShareDialog({
  open,
  onClose,
  connectionId,
  aspspName,
  groupId,
  groupName,
}: {
  open: boolean;
  onClose: () => void;
  connectionId: string;
  aspspName: string;
  groupId: string;
  groupName: string;
}) {
  const utils = trpc.useUtils();
  const { show } = useToast();

  const linkConnection = trpc.settlement.linkConnectionToCurrentPeriod.useMutation({
    onSuccess: (result) => {
      utils.settlement.list.invalidate({ groupId });
      // The auto-linked settlement now has a card source — invalidate its
      // queries so the Kortkilder panel + picker are fresh when the user
      // navigates there.
      utils.settlement.listCardSources.invalidate({ settlementId: result.settlementId });
      utils.settlement.listAvailableCards.invalidate({ settlementId: result.settlementId });
      utils.settlement.listLiveTransactions.invalidate({ settlementId: result.settlementId });
      // Også Mine kort: ekstrakort utledes fra settlement_expenses, så en
      // fersk sync kan ha avdekket et nytt ekstrakort som nå må vises.
      utils.cardConnections.listCardsForConnection.invalidate({ connectionId });
      show({
        tone: "success",
        title: t.cards.postShare.successTitle(result.fetched),
        description: t.cards.postShare.successDescription(groupName),
        dismissLabel: "OK",
      });
      onClose();
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.postShare.failed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const monthLabel = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("nb-NO", { month: "long", year: "numeric" });
  }, []);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!linkConnection.isPending) onClose();
      }}
      title={t.cards.postShare.title(aspspName)}
      description={t.cards.postShare.description(groupName, monthLabel)}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={linkConnection.isPending}>
            {t.cards.postShare.dismiss}
          </Button>
          <Button
            variant="primary"
            onClick={() => linkConnection.mutate({ connectionId, groupId })}
            isLoading={linkConnection.isPending}
          >
            {t.cards.postShare.confirm}
          </Button>
        </>
      }
    >
      {linkConnection.isPending ? (
        <div className="flex items-center gap-2 text-ink-600 text-sm">
          <Spinner size="sm" /> {t.cards.postShare.fetching}
        </div>
      ) : null}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Connect dialog — search ASPSPs, kick off BankID flow
// ---------------------------------------------------------------------------

function ConnectDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { show } = useToast();
  const aspsps = trpc.cardConnections.listAspsps.useQuery({ country: "NO" }, { enabled: open });
  // Brukerens egne tilkoblinger — brukes til å beregne "Aktiv" vs "Inaktiv"
  // pr ASPSP. Aktiv = vi har en gyldig, ikke-utløpt tilkobling. Inaktiv =
  // vi støtter det, men trenger BankID (ikke koblet eller utløpt).
  const myConnections = trpc.cardConnections.list.useQuery(undefined, { enabled: open });
  const [search, setSearch] = useState("");
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const searchInputId = useId();

  const filtered = useMemo(() => {
    // Picker viser bare ASPSPs vi har verifisert. Ikke-verifiserte filtreres
    // bort så brukeren ikke ser noe vi ikke kan koble til.
    const list = (aspsps.data ?? []).filter((a) => a.verified);
    const q = search.trim().toLocaleLowerCase("nb-NO");
    if (!q) return list;
    return list.filter((a) => a.name.toLocaleLowerCase("nb-NO").includes(q));
  }, [aspsps.data, search]);

  // Slå opp brukerens aktive tilkoblinger pr (country, name) for rask
  // state-evaluering pr ASPSP-rad.
  const myActiveByKey = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const c of myConnections.data ?? []) {
      const key = `${c.aspspCountry}|${c.aspspName}`;
      const active = c.status === "active" && !c.needsRenewal;
      // Hvis bruker har flere rader for samme ASPSP (sjeldent), én aktiv
      // er nok til at vi anser ASPSP-en som "Aktiv" i pickeren.
      if (active) map.set(key, true);
      else if (!map.has(key)) map.set(key, false);
    }
    return map;
  }, [myConnections.data]);

  const initConnect = trpc.cardConnections.initConnect.useMutation({
    onSuccess: (result) => {
      // Redirect to the aggregator's BankID flow. window.location to keep
      // session cookies intact when the aggregator bounces back to
      // /cards/callback.
      window.location.href = result.authorizationUrl;
    },
    onError: (err) => {
      show({
        tone: "danger",
        title: t.cards.connectDialog.connectFailed(err.message),
        dismissLabel: "OK",
      });
    },
  });

  const selected = filtered.find((a) => a.name === selectedName) ?? null;

  const handleConnect = () => {
    if (!selected) return;
    const redirectUrl = new URL("/cards/callback", window.location.origin).toString();
    initConnect.mutate({
      integrationId: selected.integrationId,
      aspspName: selected.name,
      aspspCountry: selected.country,
      aspspLogo: selected.logoUrl ?? null,
      redirectUrl,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t.cards.connectDialog.title}
      description={t.cards.connectDialog.description}
      footer={
        <>
          <Button onClick={onClose} disabled={initConnect.isPending}>
            {t.cards.connectDialog.cancel}
          </Button>
          <Button
            variant="primary"
            disabled={!selected || initConnect.isPending}
            isLoading={initConnect.isPending}
            onClick={handleConnect}
          >
            {t.cards.connectDialog.connect}
          </Button>
        </>
      }
    >
      <Stack gap={3}>
        <Field label={t.cards.connectDialog.issuerLabel}>
          <Input
            id={searchInputId}
            iconLeft="search"
            placeholder={t.cards.connectDialog.issuerPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Field>

        {aspsps.isPending && (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        )}

        {aspsps.isError && (
          <FieldMessage tone="error">
            {t.cards.connectDialog.loadIssuersFailed(aspsps.error.message)}
          </FieldMessage>
        )}

        {aspsps.data && (
          <AspspList
            aspsps={filtered}
            selectedName={selectedName}
            onSelect={setSelectedName}
            myActiveByKey={myActiveByKey}
            search={search}
          />
        )}

        {initConnect.isPending && (
          <FieldMessage tone="hint">{t.cards.connectDialog.redirecting}</FieldMessage>
        )}
      </Stack>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ASPSP-liste — flat picker.
//   - Bare verifiserte ASPSPs vises (filtreres oppstrøms).
//   - Ingen badges i lista; status (Aktiv/Inaktiv) lever på Mine kort-kortene.
//   - En liten check ved siden av navnet markerer "allerede tilkoblet"; den
//     på høyre side er selection-indikatoren når brukeren klikker.
//   - Alle rader er klikkbare — re-velg en allerede-tilkoblet for å
//     re-autentisere via BankID når SCA utløper.
// ---------------------------------------------------------------------------

type AspspItem = RouterOutputs["cardConnections"]["listAspsps"][number];

function AspspList({
  aspsps,
  selectedName,
  onSelect,
  myActiveByKey,
  search,
}: {
  aspsps: AspspItem[];
  selectedName: string | null;
  onSelect: (name: string) => void;
  myActiveByKey: Map<string, boolean>;
  search: string;
}) {
  if (aspsps.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-ink-200 bg-ink-50/40 p-8 text-center dark:bg-ink-100/20">
        <Icon name="search" size="md" className="text-ink-400" />
        <p className="text-ink-500 text-sm">{t.cards.connectDialog.issuerEmpty}</p>
      </div>
    );
  }

  return (
    <ul className="max-h-80 divide-y divide-ink-100 overflow-y-auto rounded-md border border-ink-200 dark:divide-ink-200">
      {aspsps.map((a) => {
        const key = `${a.country}|${a.name}`;
        return (
          <AspspRow
            key={key}
            aspsp={a}
            selected={selectedName === a.name}
            alreadyConnected={myActiveByKey.get(key) === true}
            search={search}
            onClick={() => onSelect(a.name)}
          />
        );
      })}
    </ul>
  );
}

function AspspRow({
  aspsp,
  selected,
  alreadyConnected,
  search,
  onClick,
}: {
  aspsp: AspspItem;
  selected: boolean;
  alreadyConnected: boolean;
  search: string;
  onClick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        aria-pressed={selected}
        className={cn(
          "flex w-full items-center gap-3 px-3 py-3 text-left transition-colors",
          "hover:bg-ink-50/70 focus-visible:bg-ink-50 focus-visible:outline-none dark:hover:bg-ink-100/30",
          selected && "bg-accent-50 dark:bg-accent-900/20",
        )}
        onClick={onClick}
      >
        {aspsp.logoUrl ? (
          <img
            src={aspsp.logoUrl}
            alt=""
            className="size-10 shrink-0 rounded border border-ink-200 bg-white object-contain p-1.5"
          />
        ) : (
          <div className="flex size-10 shrink-0 items-center justify-center rounded border border-ink-200 bg-ink-50 text-ink-500">
            <Icon name="credit-card" size="md" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink-900 text-sm">
            {highlightMatch(aspsp.name, search)}
          </div>
          {alreadyConnected && (
            <div className="mt-0.5 inline-flex items-center gap-1 text-emerald-700 text-xs dark:text-emerald-400">
              <Icon name="check-circle" size="xs" />
              {t.cards.connectDialog.alreadyConnectedHint}
            </div>
          )}
        </div>
        <Icon
          name={selected ? "check" : "chevron-right"}
          size="sm"
          className={cn("shrink-0", selected ? "text-accent-600" : "text-ink-400")}
        />
      </button>
    </li>
  );
}
